import { IHttpService } from "../HttpService";
import RegisteredApplicant, {
  IRegisteredApplicant
} from "../../model/applicant/RegisteredApplicant";
import ApplicantsAppointment, {
  IApplicantsAppointment,
  TApplicantsAppointment
} from "../../model/applicant/ApplicantsAppointment";
import { formatBooleanValue, parseBooleanValue } from "../..";
import ApplicantReportingPeriod, {
  IApplicantReportingPeriod
} from "../../model/applicant/ApplicantReportingPeriod";
import PortalApplicant, {
  IPortalApplicant
} from "../../model/applicant/PortalApplicant";
import ReportingPeriodPermission, {
  IReportingPeriodPermission
} from "../../model/reporting/ReportingPeriodPermission";
import UserCode, { IUserCode } from "../../model/user/UserCode";
import { DateService } from "../DateService";
import Party, { IParty } from "../../model/applicant/Party";

export interface IPartyFilterParams {
  personalId?: string;
  surname?: string;
  name?: string;
}

export interface IApplicantService {
  getRegisteredApplicants(
    applicantId?: number
  ): Promise<IRegisteredApplicant[]>;
  getApplicantsAppointments(
    applicantId: number
  ): Promise<IApplicantsAppointment[]>;
  createApplicantsAppointment(
    applicantsAppointment: TApplicantsAppointment
  ): Promise<any>;
  updateApplicantsAppointment(
    applicantsAppointment: TApplicantsAppointment
  ): Promise<any>;
  getApplicantReportingPeriods(
    applicantId
  ): Promise<IApplicantReportingPeriod[]>;
  getReportingPeriodApplicants(
    reportingPeriodId: number,
    languageCode: number
  ): Promise<IPortalApplicant[]>;
  getApplicantReportingPeriodPermissions(
    applicantId: number,
    applicantReportingPeriodId: number
  ): Promise<IReportingPeriodPermission[]>;
  createApplicantReportingPeriodPermission(
    applicantId: number,
    reportingPeriodPermission: IReportingPeriodPermission
  ): Promise<any>;
  updateApplicantReportingPeriodPermission(
    applicantId: number,
    reportingPeriodPermission: IReportingPeriodPermission
  ): Promise<any>;
  getAuthorizedOperators(
    applicantReportingPeriodId: number
  ): Promise<IUserCode[]>;
  insertNewFormVersion(
    reportingPeriodApplicantId: number,
    formTypeId: number
  ): Promise<any>;
  registerApplicant(partyId: string): Promise<number>;
  getParties(params?: IPartyFilterParams): Promise<IParty[]>;
}

const ApplicantService = (httpService): IApplicantService => {
  const _http: IHttpService = httpService;
  const _basePath: string = "/api";
  const _applicantsPath: string = "applicants";
  const _applicantsAppointmentsPath = "appointments";
  const _applicantReportingPeriodsPath = "reporting-periods";
  const _reportingPeriodsPath = "reporting-periods";
  const _permissionsPath = "permissions";
  const _authorizedOperatorsPath = "authorized-operators";
  const _typePath = "type";
  const _partiesPath = "parties";

  async function buildApplicantList(
    data: any
  ): Promise<IRegisteredApplicant[]> {
    return Promise.all(data.map(RegisteredApplicant));
  }

  async function buildReportingPeriodPermissionList(
    data: any
  ): Promise<IReportingPeriodPermission[]> {
    return Promise.all(
      data.map((item: any) => {
        let reportingPeriodPermission = ReportingPeriodPermission(item);
        return reportingPeriodPermission;
      })
    );
  }

  function buildUserCodeList(data: any): IUserCode[] {
    return data.map(UserCode);
  }

  async function buildApplicantsAppointmentList(
    data: any
  ): Promise<IApplicantsAppointment[]> {
    return Promise.all(
      data.map((item: any) => {
        let applicantsAppointment = ApplicantsAppointment(item);
        applicantsAppointment.appointmentValid = formatBooleanValue(
          item.appointmentValid
        );
        return applicantsAppointment;
      })
    );
  }

  const ApplicantReportingPeriodListMapper = (json) => {
    return (json || []).map((item) => {
      let value = ApplicantReportingPeriod(item);
      value.isEvaluationCandidate = formatBooleanValue(
        item.isEvaluationCandidate
      );
      value.hasRightOfEntry = !!item.hasRightOfEntry;
      value.hasRightOfPreview = !!item.hasRightOfPreview;
      return value;
    });
  };

  const parseDateToTimestamp = (date?: Date) => {
    if (date) {
      return DateService().parseDateToTimestamp(date);
    }
  };

  const AppointmentParamsMapper = (
    applicantsAppointment: TApplicantsAppointment
  ) => {
    return {
      applicantId: applicantsAppointment.applicantId,
      applicantsAppointmentId: applicantsAppointment.applicantsAppointmentId,
      appointmentDate: parseDateToTimestamp(
        applicantsAppointment.appointmentDate
      ),
      appointmentId: applicantsAppointment.appointmentId,
      description: applicantsAppointment.description,
      endMandate: parseDateToTimestamp(applicantsAppointment.endMandate),
      id: applicantsAppointment.id,
      positionTypeId: applicantsAppointment.positionTypeId,
      predictedEnd: parseDateToTimestamp(applicantsAppointment.predictedEnd),
      sourceData: applicantsAppointment.sourceData,
      startMandate: parseDateToTimestamp(applicantsAppointment.startMandate),
      valid: parseBooleanValue(applicantsAppointment.appointmentValid)
    };
  };

  const PermissionParamsMapper = (permission: IReportingPeriodPermission) => {
    return {
      ...permission,
      fromDate: parseDateToTimestamp(permission.fromDate),
      toDate: parseDateToTimestamp(permission.toDate)
    };
  };

  const portalApplicantListMapper = (json: any = []) => {
    return json.map((item: any) => {
      return PortalApplicant(item);
    });
  };

  const insertNewFormVersionResponse = (json: any = []) => {
    return {
      formReportingPeriodId: json.formReportingPeriodId,
      id: json.id,
      version: json.version
    };
  };

  const partiesMapper = (json: any[] = []) => {
    return json.map(Party);
  };

  return {
    async getRegisteredApplicants(applicantId?: number) {
      const path = _http.buildPath(_basePath, _applicantsPath);
      const response = await _http.get(path, {
        params: {
          applicantId
        }
      });
      const responseJSON = await _http.toJSON(response);
      return buildApplicantList(responseJSON);
    },

    async getApplicantsAppointments(applicantId: number) {
      const path = _http.buildPath(
        _basePath,
        _applicantsPath,
        applicantId.toString(),
        _applicantsAppointmentsPath
      );
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return buildApplicantsAppointmentList(responseJSON);
    },

    async createApplicantsAppointment(
      applicantsAppointment: TApplicantsAppointment
    ) {
      const path = _http.buildPath(
        _basePath,
        _applicantsPath,
        _applicantsAppointmentsPath
      );
      const response = await _http.post(path, {
        params: AppointmentParamsMapper(applicantsAppointment)
      });
      return _http.toText(response);
    },

    async updateApplicantsAppointment(
      applicantsAppointment: TApplicantsAppointment
    ) {
      const path = _http.buildPath(
        _basePath,
        _applicantsPath,
        _applicantsAppointmentsPath,
        applicantsAppointment.id.toString()
      );
      const response = await _http.put(path, {
        params: AppointmentParamsMapper(applicantsAppointment)
      });
      return _http.toText(response);
    },

    async getApplicantReportingPeriods(applicantId) {
      const path = _http.buildPath(
        _basePath,
        _applicantsPath,
        applicantId.toString(),
        _applicantReportingPeriodsPath
      );
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return ApplicantReportingPeriodListMapper(responseJSON);
    },

    async getReportingPeriodApplicants(
      reportingPeriodId: number,
      languageCode: number
    ) {
      const path = _http.buildPath(
        _basePath,
        _applicantsPath,
        _reportingPeriodsPath,
        reportingPeriodId.toString()
      );
      const response = await _http.get(path, {
        params: {
          languageCode
        },
        form: true
      });
      const responseJSON = await _http.toJSON(response);
      return portalApplicantListMapper(responseJSON);
    },

    async getApplicantReportingPeriodPermissions(
      applicantId: number,
      applicantReportingPeriodId: number
    ) {
      const path = _http.buildPath(
        _basePath,
        _applicantsPath,
        applicantId.toString(),
        _reportingPeriodsPath,
        applicantReportingPeriodId.toString(),
        _permissionsPath
      );
      const response = await _http.get(path, {});
      const responseJSON = await _http.toJSON(response);
      return buildReportingPeriodPermissionList(responseJSON);
    },

    async createApplicantReportingPeriodPermission(
      applicantId: number,
      reportingPeriodPermission: IReportingPeriodPermission
    ) {
      const path = _http.buildPath(
        _basePath,
        _applicantsPath,
        applicantId.toString(),
        _reportingPeriodsPath,
        reportingPeriodPermission.applicantReportingPeriodId.toString(),
        _permissionsPath
      );
      const reponse = await _http.post(path, {
        params: PermissionParamsMapper(reportingPeriodPermission)
      });
      return _http.toText(reponse);
    },

    async updateApplicantReportingPeriodPermission(
      applicantId: number,
      reportingPeriodPermission: IReportingPeriodPermission
    ) {
      const path = _http.buildPath(
        _basePath,
        _applicantsPath,
        applicantId.toString(),
        _reportingPeriodsPath,
        reportingPeriodPermission.id.toString(),
        _permissionsPath
      );
      const response = await _http.put(path, {
        params: PermissionParamsMapper(reportingPeriodPermission)
      });
      return _http.toText(response);
    },

    async getAuthorizedOperators(applicantReportingPeriodId: number) {
      const path = _http.buildPath(
        _basePath,
        _applicantsPath,
        applicantReportingPeriodId.toString(),
        _authorizedOperatorsPath
      );
      const response = await _http.get(path);
      const responseJSON = await _http.toJSON(response);
      return buildUserCodeList(responseJSON);
    },

    async insertNewFormVersion(
      reportingPeriodApplicantId: number,
      formTypeId: number
    ) {
      const path = _http.buildPath(
        _basePath,
        _applicantsPath,
        _reportingPeriodsPath,
        reportingPeriodApplicantId.toString(),
        _typePath,
        formTypeId.toString()
      );
      const response = await _http.post(path);
      const responseJSON = await _http.toJSON(response);
      return insertNewFormVersionResponse(responseJSON);
    },

    async registerApplicant(partyId: string) {
      const path = _http.buildPath(_basePath, _applicantsPath);
      const response = await _http.post(path, {
        form: true,
        params: {
          partyId
        }
      });
      return Number(response);
    },

    async getParties(params?: IPartyFilterParams) {
      const path = _http.buildPath(_basePath, _applicantsPath, _partiesPath);
      const response = await _http.get(path, {
        params
      });
      const reponseJSON = await _http.toJSON(response);
      return partiesMapper(reponseJSON);
    }
  };
};

export default ApplicantService;
